<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/payouts/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    >
      <div class="mt-16 text-red text-body-big">
        {{ $t('toolbar.export.downloadPainFile.text') }}
      </div>
      <Multiselect
        v-model="painFileAccount"
        :options="painFileOptions"
        :label="$t('toolbar.export.downloadPainFile.dropdown.DEFAULT')"
        :can-deselect="false"
        :can-clear="false"
        class="mt-3 mb-5"
      />
      <ExportLink
        v-if="painFileAccount && checkedIdsList.length > 0"
        :custom-export="exportPainFile"
        :label="$t('toolbar.export.downloadPainFile.button')"
        class="mt-5"
      />
      <div v-else-if="checkedIdsList.length === 0">
        {{ $t('toolbar.export.downloadPainFile.noPayoutsSelected') }}
      </div>

      <div class="mt-16 mb-4 font-bold">
        {{ $t('toolbar.export.mergePainFiles.button') }}
      </div>
      <button
        type="button"
        class="export-link mt-3"
        data-cy="campaign-payouts-merge-pain-btn"
        @click.prevent.stop="openMergePainFilesModal = true"
      >
        <ExportIcon class="flex-0 h-5.5" />
        <label>{{ $t('toolbar.export.mergePainFiles.combine') }}</label>
      </button>
    </export>
    <action
      v-if="checkedIdsList.length > 0"
      :icon="SuccessIcon"
      :label="$t('pages.driver.payoutList.markAsPayed')"
      @click="openBulkEditPayoutStatusModal = true"
    />
  </toolbar>
  <edit-payout-modal
    v-model:open="openEditPayoutModal"
    :edit-payout="editPayout"
    @saved="emit('reload')"
  />
  <payout-bulk-status-change-modal
    :campaign-group="modelValue"
    :payouts="records"
    :payout-ids="checkedIdsList"
    v-model:open="openBulkEditPayoutStatusModal"
    @saved="refetch"
  />
  <merge-pain-files-modal v-model:open="openMergePainFilesModal" />

  <div class="ml-12">
    <PayoutStatisticsTable
      :statistics="{
        total: filteredStatistics?.total,
        filtered: filteredStatistics?.filtered,
        max: filteredStatistics?.max,
        selected: selectedPayoutStatistics,
      }"
      :loading="loadingStatistics"
      :admins-are-allowed="true"
    />

    <div v-if="isFinanceAdmin" class="text-body-regular-bold text-table-text-gray uppercase mb-5">
      {{ $t('pages.driver.payoutList.tableHeader') }}
    </div>
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :row-link="(reg) => ({ name: 'driver-details', params: { driverId: reg?.driver.id } })"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="actions"
        :choosable="true"
        v-model:checked-ids-list="checkedIdsList"
      />
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
    :per-page-choices="[5, 10, 50, 100, 1000]"
  />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, type Ref, ref, unref} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import SuccessIcon from '@images/success.svg?component';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, {type Column} from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider, withExtraOptions } from '@utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter} from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  CampaignGroup,
  HydraCollection,
  Payout,
  PayoutEntry,
  SpecialDriverAssignment
} from '@/entities/Entities';
import {Download, Reward} from '@/entities/Entities';
import EditPayoutModal from '@pages/modals/EditPayoutModal.vue';
import {FetchError, useFetchAction} from '@hooks/fetch';
import {useNotify} from '@utils/notifications';
import {useDownloads} from '@hooks/tasks';
import {useConfirmPrompt} from '@utils/prompt/injects';
import Action from '@utils/toolbar/Action.vue';
import ExportLink from '@utils/toolbar/panes/ExportLink.vue';
import {useQueryRef} from '@/hooks/url';
import PayoutBulkStatusChangeModal
  from '@pages/campaigns/campaignGroup/PayoutBulkStatusChangeModal.vue';
import MergePainFilesModal
  from '@pages/campaigns/campaignGroup/MergePainFilesModal.vue';
import ExportIcon from '@images/export.svg?component';
import type {Statistics} from '@utils/PayoutStatisticsTable.vue';
import PayoutStatisticsTable from '@utils/PayoutStatisticsTable.vue';
import {useStore} from '@/store';
import {useTableFields} from '@hooks/table-configuration';
import {calculateSelectedPayoutStatistics} from '@/hooks/payout';
import {formatNumberForCurrency} from '@/utils/helpers';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue?: CampaignGroup;
}

const props = defineProps<Props>();

const emit = defineEmits(['reload']);

const checkedIdsList = ref<string[]>([]);

const checkedPayouts = computed(
  () => records.value?.filter((payout: Payout) => checkedIdsList.value.includes(payout.id)) ?? []
);

const auth = useStore();
const isFinanceAdmin = computed(() => auth.getters['auth/IS_FINANCE_ADMIN']);

const selectedPayoutStatistics = calculateSelectedPayoutStatistics(checkedPayouts);

const notify = useNotify();

const csvTitle = computed(() => {
  return t('csv.title.payoutsFromGroup', {
    group: props.modelValue?.name,
  });
});

const [exec, { loading: loadingPainFile }] = useFetchAction();
const downloads = useDownloads();

const confirm = useConfirmPrompt();

const painFileAccount = ref();
const painFileOptions = enumOptionsProvider('/enums/WBBankAccounts', {}, (item) => ({
  label: t(`toolbar.export.downloadPainFile.dropdown.${item}`),
  value: item,
}));

const exportPainFile = async (ignoreAlreadyExported: boolean = false) => {
  if (!painFileAccount.value) {
    return;
  }
  try {
    const download = await exec<Download>(`${props.modelValue?.['@id']}/pain`, {
      method: 'POST',
      body: {
        ignoreAlreadyExported,
        account: painFileAccount.value,
        payoutIds: checkedIdsList.value,
      },
    });
    downloads?.addTask(download);
    notify?.({
      variant: 'success',
      title: t('toolbar.export.successRequest'),
    });
    refetch();
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === 'no_payouts') {
        notify?.({
          variant: 'info',
          title: t('painExport.noPayouts'),
        });
        return;
      } else if (errorCode?.error?.key === 'already_exported' && !ignoreAlreadyExported) {
        const answer = await confirm?.(t('painExport.alreadyExportedConfirmation'));
        if (answer) {
          await exportPainFile(true);
        }
        return;
      }
    }
    notify?.({
      variant: 'error',
      title: t('toolbar.export.errorRequest'),
    });
  }
};

const openBulkEditPayoutStatusModal = ref(false);
const openMergePainFilesModal = ref(false);

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 1000);
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const openEditPayoutModal = ref(false);
const editPayout = ref();

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaignRegistration.campaign.group': props.modelValue?.id ?? '',
  'inArray[campaignRegistration.campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  const convertedFilter: any = { ...filter.value };
  if (Array.isArray(convertedFilter['campaignRegistration.specialDriverAssignments.type']) && convertedFilter['campaignRegistration.specialDriverAssignments.type'].find((item: string) => item === 'none')) {
    delete convertedFilter['campaignRegistration.specialDriverAssignments.type'];
    return {
      ...fixedFilter.value,
      ...convertedFilter,
      'campaignRegistration.specialDriverAssignments.type[lte]': '0',
    };
  }
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.driver.payoutList.actions.edit'),
    action: (item: Payout) => {
      editPayout.value = { ...item, entries: item.entries.map((entry) => ({ ...entry })) };
      openEditPayoutModal.value = true;
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: true,
  },
  {
    key: 'executionTimestamp',
    title: t('payout.executionDate'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('payout.executionDate'),
      },
    },
  },
  {
    key: 'paidDate',
    title: t('payout.paidDate'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('payout.paidDate'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaignRegistration.campaign',
      type: 'dropdown',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'campaignRegistration.campaign.timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT2'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (p: Payout) => {
      const now = new Date();
      const startDate = new Date(p.campaignRegistration.campaign.actualStartDate);
      const endDate = new Date(p.campaignRegistration.campaign.actualEndDate);
      if (startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if (startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      key: 'timeStatus',
      type: 'dropdown',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT2'),
      },
    },
  },
  {
    key: 'paymentAmount',
    title: t('payout.amount'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    formatter: (v: number) => v.toFixed(2).padStart(5, '0') + ' CHF',
    filter: {
      type: 'range',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        icon: <span></span>,
        label: t('payout.amount'),
      },
    },
  },
  {
    key: 'paidCredits',
    title: t('payout.paidCredits'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    computed: (payout: Payout) => {
      const amount = payout.entries
        ?.filter((entry: PayoutEntry) => entry.type === 'credit')
        .reduce((acc: number | null, item) => (acc ?? 0) + item.amount, null)
        ?.toFixed(2)
        .padStart(5, '0');
      return amount ? amount + ' CHF' : '-';
    },
    filter: {
      type: 'range',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        icon: <span>CHF</span>,
        label: t('payout.paidCredits'),
      },
    },
  },
  {
    key: 'paidDebts',
    title: t('payout.paidDebts'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    computed: (payout: Payout) => {
      const amount = payout.entries
        ?.filter((entry: PayoutEntry) => entry.type === 'debt')
        .reduce((acc: number | null, item) => (acc ?? 0) + item.amount, null)
        ?.toFixed(2)
        .padStart(5, '0');
      return amount ? amount + ' CHF' : '-';
    },
    filter: {
      type: 'range',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        icon: <span>CHF</span>,
        label: t('payout.paidDebts'),
      },
    },
  },
  {
    key: 'campaignRegistration.openDebts',
    title: t('driver.openDebts'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'CHF',
    formatter: item => item === null ? '-' : formatNumberForCurrency(item),
    filter: {
      type: 'range',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('driver.openDebts'),
        icon: <span>CHF</span>,
      },
    },
  },
  {
    key: 'status',
    csvTranslations: {
      pending: t('payoutStatus.pending'),
      inPayment: t('payoutStatus.inPayment'),
      payed: t('payoutStatus.payed'),
      error: t('payoutStatus.error'),
    },
    title: t('payout.status'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`payoutStatus.${item}`),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/PayoutStatus', (item) => ({
          value: item,
          label: t(`payoutStatus.${item}`),
        })),
        label: t('payout.status'),
      },
    },
  },
  {
    key: 'includeIntoBankDocument',
    title: t('payout.includeIntoBankDocument'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => (v ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('payout.includeIntoBankDocument'),
      },
    },
  },
  {
    key: 'comment',
    title: t('payout.comment'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        label: t('payout.comment'),
      },
    },
  },
  {
    key: 'rewards',
    csvTranslations: {
      top: t('reward.type.top'),
      photo: t('reward.type.photo'),
      focusPoint: t('reward.type.focusPoint'),
    },
    title: t('payout.rewards'),
    group: t('pages.campaigns.payoutList.fieldGroups.general'),
    sortable: true,
    sortKey: 'rewards.rewardType',
    visible: true,
    formatter: (item: Reward[]) => item.map((r) => t(`reward.type.${r.rewardType}`)).join(', '),
    filter: {
      key: 'rewards.rewardType',
      type: 'dropdown',
      group: t('pages.campaigns.payoutList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/RewardType', (item) => ({
          value: item,
          label: t(`reward.type.${item}`),
        })),
        label: t('reward.type.DEFAULT'),
      },
    },
  },
  {
    key: 'campaignRegistration.specialDriverAssignments',
    csvTranslations: {
      none: t('campaignRegistration.specialDriverAssignment.type.none'),
      photo: t('campaignRegistration.specialDriverAssignment.type.photo'),
      standing: t('campaignRegistration.specialDriverAssignment.type.standing'),
    },
    title: t('campaignRegistration.specialDriverAssignment.types'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: SpecialDriverAssignment[] | null) => {
      if (v && v.length > 0) {
        return v
          .map((item) => t(`campaignRegistration.specialDriverAssignment.type.${item.type}`))
          .join(', ');
      } else {
        return t('campaignRegistration.specialDriverAssignment.type.none');
      }
    },
    filter: {
      type: 'tags',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      key: 'campaignRegistration.specialDriverAssignments.type',
      options: {
        closeOnSelect: false,
        options: withExtraOptions(enumOptionsProvider('/enums/specialAssignmentType', (v) => ({
          label: t(`campaignRegistration.specialDriverAssignment.type.${v}`),
          value: v,
        })), [
          { label: t('campaignRegistration.specialDriverAssignment.type.none'), value: 'none' },
        ]),
        label: t('campaignRegistration.specialDriverAssignment.types'),
      },
    },
  },
  {
    key: 'campaignRegistration.completedSpecialDriverAssignments',
    csvTranslations: {
      none: t('campaignRegistration.specialDriverAssignment.type.none'),
      photo: t('campaignRegistration.specialDriverAssignment.type.photo'),
      standing: t('campaignRegistration.specialDriverAssignment.type.standing'),
    },
    title: t('campaignRegistration.specialDriverAssignment.completed'),
    group: t('pages.campaigns.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: SpecialDriverAssignment[] | null) => {
      if (v && v.length > 0) {
        return v
          .map((item) => t(`campaignRegistration.specialDriverAssignment.type.${item}`))
          .join(', ');
      } else {
        return t('campaignRegistration.specialDriverAssignment.type.none');
      }
    },
    filter: {
      type: 'tags',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        closeOnSelect: false,
        options: withExtraOptions(enumOptionsProvider('/enums/specialAssignmentType', (v) => ({
          label: t(`campaignRegistration.specialDriverAssignment.type.${v}`),
          value: v,
        })), [
          { label: t('campaignRegistration.specialDriverAssignment.type.none'), value: 'none' },
        ]),
        label: t('campaignRegistration.specialDriverAssignment.completed'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<Statistics>(
  '/payouts/statistics',
  computed(() => ({
    ...providerParams,
    special: {
      campaign_group: props.modelValue?.id ?? '',
    },
  })),
  {
    disabled: campaignGroupNeedsLoading,
  }
);

const {
  data: items,
  loading,
  refetch: refetchItems,
} = useProvider<HydraCollection<Payout>>('/payouts', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const refetch = () => {
  refetchItems();
  refetchStatistics();
};

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

.export-link {
  @apply flex gap-x-3.5 items-center;
  @apply text-left text-body-regular font-normal;
  @apply cursor-pointer;

  max-width: px(350);

  label {
    @apply cursor-pointer;
  }
}
</style>
